import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

const FirmSettings = () => {
  const api = useAPI();
  const navigate = useNavigate();
  const { sessionStatus } = useAuth();
  const [loading, setLoading] = useState(true);
  const [firmName, setFirmName] = useState('');
  const [enableQA, setEnableQA] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (sessionStatus === 'expired') {
      navigate('/login');
    } else if (sessionStatus === 'error') {
      toast.error('Having trouble connecting to the server. Will retry shortly...');
    }
  }, [sessionStatus, navigate]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get('/firm/settings');
      setFirmName(data.name || '');
      setEnableQA(data.settings?.enable_qa || false);
    } catch (error) {
      console.error('Error fetching firm settings:', error);
      toast.error('Failed to load firm settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put('/firm/settings', {
        name: firmName,
        settings: {
          enable_qa: enableQA
        }
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving firm settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Firm Settings</h2>
        </div>
        
        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Firm Name
            </label>
            <input
              type="text"
              value={firmName}
              onChange={(e) => setFirmName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter firm name"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Case Review Settings</h3>
            
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="enableQA"
                checked={enableQA}
                onChange={(e) => setEnableQA(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="enableQA" className="text-sm text-gray-700">
                Enable QA Review
              </label>
            </div>
            <p className="text-sm text-gray-500 italic">
              When enabled, analysts cannot mark cases as Finished directly. A manager must review and approve the case first.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              isSaving ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirmSettings; 